'use client';

import {
  NonSegmentEvents,
  buildEventProperties,
  trackD2CNonSegmentAnalytics,
} from '@/analytics';
import { useAuthorizedCustomerSession } from '@/hooks/authorized-operations';
import { usePathSegments } from '@/hooks/usePathSegments';
import { useProductConfig } from '@/hooks/useProductConfig';
import { Button, Icon } from '@/lib/mx-asurion-ui-react';
import { getStepInfo } from '@sales-domain/product-config';
import { type CustomerSession } from '@soluto-private/asp-core';
import { useRouter } from 'next/navigation';
import './BackButton.css';

function trackAnalytics({
  eventName,
  customerSession,
  currentStep,
}: {
  eventName: NonSegmentEvents;
  customerSession?: CustomerSession;
  currentStep: string;
}) {
  trackD2CNonSegmentAnalytics(
    eventName,
    buildEventProperties({
      event: eventName,
      customerSession,
      extraData: {
        error: eventName,
        currentPage: currentStep,
      },
    }),
  );
}

export type PrepaidBackButtonProps = React.HTMLAttributes<HTMLButtonElement> & {
  prevPage: string;
  backText?: string;
};

export function PrepaidBackButton(props: PrepaidBackButtonProps) {
  const pathSegments = usePathSegments();
  const { channelConfigs } = useProductConfig();
  const router = useRouter();
  const { customerSession } = useAuthorizedCustomerSession();

  async function handleBack() {
    const configSteps =
      channelConfigs.DirectToConsumer?.appRouteConfig?.steps ?? [];
    const currentStep = pathSegments.currentStep ?? '';
    const stepNavigationConfig = getStepInfo(configSteps, currentStep);
    const prevPage = stepNavigationConfig?.backPage;

    if (!prevPage) {
      trackAnalytics({
        eventName: NonSegmentEvents.ConfigMissingBackPage,
        customerSession,
        currentStep,
      });
      router.replace(`./error`);
    } else {
      router.push(`./${props.prevPage}`);
    }
  }

  const arrowIcon = <Icon src="arrow-left" size="large" />;

  return (
    <Button
      onClick={handleBack}
      {...props}
      variant="outline"
      color="secondary"
      className="button-border"
    >
      <div className="flex items-center justify-between">
        <div className="mr-2">{arrowIcon}</div>
        <div className="mr-1">{props.backText}</div>
      </div>
    </Button>
  );
}
